import { z } from 'zod';

export const unitKindSchema = z.enum(['room', 'public-area', 'event-hall']);
export type UnitKind = z.infer<typeof unitKindSchema>;
export const spaceKindSchema = unitKindSchema;
export type SpaceKind = UnitKind;

export const panModeSchema = z.enum(['private', 'shared-group', 'public']);
export type PanMode = z.infer<typeof panModeSchema>;

export const codeStatusSchema = z.enum(['active', 'scheduled', 'expired', 'revoked']);
export type CodeStatus = z.infer<typeof codeStatusSchema>;

export const provisionSourceSchema = z.enum(['pms', 'reception', 'bulk']);
export type ProvisionSource = z.infer<typeof provisionSourceSchema>;

export const deliveryChannelSchema = z.enum(['tv', 'email', 'dashboard', 'print']);
export type DeliveryChannel = z.infer<typeof deliveryChannelSchema>;

export const accessCodeCategorySchema = z.enum([
  'guest',
  'visitor',
  'iot',
  'staff',
  'journalist',
  'relator'
]);
export type AccessCodeCategory = z.infer<typeof accessCodeCategorySchema>;

export const eventStatusSchema = z.enum(['scheduled', 'active', 'expired', 'disabled']);
export type EventStatus = z.infer<typeof eventStatusSchema>;

export const queueStatusSchema = z.enum(['queued', 'running', 'completed', 'attention']);
export type QueueStatus = z.infer<typeof queueStatusSchema>;

export const userRoleSchema = z.enum([
  'platform-admin',
  'tenant-admin',
  'reception',
  'operations',
  'tv-integration'
]);
export type UserRole = z.infer<typeof userRoleSchema>;

export const vendorFieldTypeSchema = z.enum(['string', 'number', 'boolean', 'password']);
export type VendorFieldType = z.infer<typeof vendorFieldTypeSchema>;
export const vendorFieldTypeOptions = vendorFieldTypeSchema.options;
export const vendorConfigurationValueSchema = z.union([z.string(), z.number(), z.boolean()]);
export type VendorConfigurationValue = z.infer<typeof vendorConfigurationValueSchema>;

export const locationCategorySchema = z.enum(['hotel', 'resort', 'conference']);
export const locationStateSchema = z.enum([
  'active',
  'disabled',
  'pending',
  'stable',
  'attention'
]);
export type LocationState = z.infer<typeof locationStateSchema>;
export const locationAdministrationStateSchema = z.enum(['active', 'disabled', 'pending']);
export type LocationAdministrationState = z.infer<typeof locationAdministrationStateSchema>;
export const unitAssignmentSchema = z.enum(['room', 'group', 'common']);
export type UnitAssignment = z.infer<typeof unitAssignmentSchema>;
export const spaceAssignmentSchema = unitAssignmentSchema;
export type SpaceAssignment = UnitAssignment;
export const bulkModeSchema = z.enum(['shared-vlan', 'isolated']);
export const unitStatusSchema = z.enum(['available', 'disabled', 'occupied']);
export type UnitStatus = z.infer<typeof unitStatusSchema>;
export const userStatusSchema = z.enum(['active', 'invited', 'disabled']);
export const alertSeveritySchema = z.enum(['low', 'medium', 'high']);
export const authenticatedUserModeSchema = z.enum(['mock', 'keycloak']);

const normalizedTenantTextSchema = z.string().nullish().transform((value) => value ?? '');
export const vendorFieldMapSchema = z
  .record(z.string(), vendorFieldTypeSchema)
  .superRefine((fields, ctx) => {
    const names = Object.keys(fields);

    if (!names.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one vendor field is required.'
      });
    }

    for (const name of names) {
      if (!name.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Vendor field names must be non-empty.'
        });
      }
    }
  });
export type VendorFieldMap = z.infer<typeof vendorFieldMapSchema>;
export const vendorConfigurationSchema = z.record(z.string(), vendorConfigurationValueSchema);
export type VendorConfiguration = z.infer<typeof vendorConfigurationSchema>;

export const vendorBindingSchema = z.object({
  vendorId: z.string(),
  configuration: vendorConfigurationSchema
});
export type VendorBinding = z.infer<typeof vendorBindingSchema>;
export const optionalVendorBindingSchema = vendorBindingSchema
  .nullish()
  .transform<VendorBinding | null>((value) => {
    if (!value || !value.vendorId.trim()) {
      return null;
    }

    return {
      vendorId: value.vendorId.trim(),
      configuration: value.configuration
    };
  });
export const vendorBindingResponseSchema = z
  .union([
    z.string(),
    z.null(),
    z.undefined(),
    z.object({
      vendorId: z.string(),
      configuration: vendorConfigurationSchema.optional().default({})
    })
  ])
  .transform<VendorBinding | null>((value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      const vendorId = value.trim();
      return vendorId
        ? {
            vendorId,
            configuration: {}
          }
        : null;
    }

    const vendorId = value.vendorId.trim();
    return vendorId
      ? {
          vendorId,
          configuration: value.configuration
        }
      : null;
  });

const vendorCatalogSchema = z.object({
  id: z.string(),
  name: z.string(),
  fields: vendorFieldMapSchema
});

export const tenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: normalizedTenantTextSchema,
  sla: normalizedTenantTextSchema,
  properties: z.number(),
  dpskCoverage: z.number(),
  openAlerts: z.number()
});
export type Tenant = z.infer<typeof tenantSchema>;

export const controllerVendorSchema = vendorCatalogSchema;
export type ControllerVendor = z.infer<typeof controllerVendorSchema>;

export const pmsVendorSchema = vendorCatalogSchema;
export type PmsVendor = z.infer<typeof pmsVendorSchema>;

export const locationSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  city: z.string(),
  category: locationCategorySchema,
  ssid: z.string().nullish().transform((value) => value?.trim() ?? ''),
  pms: vendorBindingResponseSchema,
  controller: vendorBindingResponseSchema,
  tvIntegration: z.boolean(),
  emailIntegration: z.boolean(),
  state: locationStateSchema,
  dailyCheckIns: z.number().optional(),
  activeGuests: z.number().optional(),
  bulkQueue: z.number().optional(),
  note: z.string(),
  timezone: z.string()
});
export type Location = z.infer<typeof locationSchema>;

export const unitSchema = z.object({
  id: z.string(),
  locationId: z.string(),
  name: z.string(),
  type: unitKindSchema,
  vlanId: z.number(),
  serviceVlanId: z.number().nullable().optional().default(null),
  networkId: z.string().nullable().optional().default(null),
  ssid: z.string().optional(),
  panMode: panModeSchema,
  capacity: z.number(),
  assignment: unitAssignmentSchema,
  status: unitStatusSchema.optional().default('available')
});
export type Unit = z.infer<typeof unitSchema>;
export const spaceSchema = unitSchema;
export type Space = Unit;

const normalizeDateTime = (value: string): string => value.replace(/\[[^\]]+\]$/, '');

export const accessCodeSchema = z.object({
  id: z.string(),
  guestName: z.string(),
  email: z.string(),
  locationId: z.string(),
  spaceId: z.string(),
  source: provisionSourceSchema,
  category: accessCodeCategorySchema.nullish().transform((value) => value ?? null),
  deliveryChannels: z.array(deliveryChannelSchema),
  status: codeStatusSchema,
  passphrase: z.string(),
  validFrom: z.string().transform(normalizeDateTime),
  validUntil: z.string().transform(normalizeDateTime),
  devicesAllowed: z.number(),
  devicesConnected: z.number(),
  lastSeenAt: z.string().nullable().optional(),
  qrPayload: z.string(),
  bookingReference: z.string(),
  serviceMode: z.boolean().optional().default(false),
  tags: z.array(z.string()),
  notes: z.string().nullable().optional()
});
export type AccessCode = z.infer<typeof accessCodeSchema>;

export const hospitalityEventSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  locationId: z.string(),
  name: z.string(),
  spaceId: z.string(),
  networkId: z.string(),
  ssid: z.string(),
  validFrom: z.string(),
  validUntil: z.string(),
  isolation: z.boolean(),
  policyId: z.string().nullable(),
  code: z.string(),
  status: eventStatusSchema,
  contactName: z.string(),
  contactEmail: z.string(),
  deliveryChannels: z.array(z.enum(['email', 'dashboard', 'print']))
});
export type HospitalityEvent = z.infer<typeof hospitalityEventSchema>;

export const hospitalityEventDraftSchema = hospitalityEventSchema.omit({
  id: true,
  tenantId: true,
  locationId: true,
  code: true,
  status: true
});
export type HospitalityEventDraft = z.infer<typeof hospitalityEventDraftSchema>;

export const eventNetworkSchema = z.object({
  networkId: z.string(),
  name: z.string(),
  ssid: z.string().nullable()
});
export type EventNetwork = z.infer<typeof eventNetworkSchema>;

export const bulkJobSchema = z.object({
  id: z.string(),
  locationId: z.string(),
  fileName: z.string(),
  requestedBy: z.string(),
  createdAt: z.string(),
  total: z.number(),
  processed: z.number(),
  mode: bulkModeSchema,
  queueStatus: queueStatusSchema,
  targetVlan: z.number().nullable().optional()
});
export type BulkJob = z.infer<typeof bulkJobSchema>;

export const userScopeSchema = z.object({
  tenantId: z.string(),
  locationIds: z.array(z.string())
});
export type UserScope = z.infer<typeof userScopeSchema>;

export const userScopeDraftSchema = z.object({
  tenantId: z.string(),
  locationIds: z.array(z.string())
});
export type UserScopeDraft = z.infer<typeof userScopeDraftSchema>;
function normalizeUserScopes(scopes: ReadonlyArray<UserScopeDraft>): UserScope[] {
  const dedupedScopes = new Map<string, UserScope>();

  for (const scope of scopes) {
    dedupedScopes.set(scope.tenantId, {
      tenantId: scope.tenantId,
      locationIds: [...new Set(scope.locationIds)]
    });
  }

  return [...dedupedScopes.values()];
}

export const appUserSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    role: userRoleSchema,
    scopes: z.array(userScopeSchema),
    status: userStatusSchema
  })
  .transform((value) => {
    return {
      ...value,
      scopes: normalizeUserScopes(value.scopes)
    };
  });
export type AppUser = z.infer<typeof appUserSchema>;

export const dashboardAlertSchema = z.object({
  id: z.string(),
  locationId: z.string(),
  severity: alertSeveritySchema,
  title: z.string(),
  description: z.string()
});
export type DashboardAlert = z.infer<typeof dashboardAlertSchema>;

export const dashboardSnapshotSchema = z.object({
  tenants: z.array(tenantSchema),
  locations: z.array(locationSchema),
  spaces: z.array(unitSchema),
  codes: z.array(accessCodeSchema),
  events: z.array(hospitalityEventSchema).optional().default([]),
  bulkJobs: z.array(bulkJobSchema),
  users: z.array(appUserSchema),
  alerts: z.array(dashboardAlertSchema)
});
export type DashboardSnapshot = z.infer<typeof dashboardSnapshotSchema>;

export const manualCodeDraftSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  locationId: z.string(),
  spaceId: z.string().trim().min(1),
  category: accessCodeCategorySchema.optional().default('guest'),
  validFrom: z.string(),
  validUntil: z.string(),
  devicesAllowed: z.number(),
  deliveryChannels: z.array(deliveryChannelSchema),
  bookingReference: z.string().optional().default(''),
  ssid: z.string().optional(),
  serviceMode: z.boolean().optional().default(false),
  notes: z.string().optional()
});
export type ManualCodeDraft = z.infer<typeof manualCodeDraftSchema>;

export const groupCodeDraftSchema = z.object({
  groupName: z.string(),
  contactName: z.string(),
  contactEmail: z.string(),
  sendEmail: z.boolean(),
  category: accessCodeCategorySchema,
  validFrom: z.string(),
  validUntil: z.string(),
  devicesAllowed: z.number(),
  deliveryChannels: z.array(deliveryChannelSchema),
  ssid: z.string().optional(),
  serviceMode: z.boolean().optional().default(false),
  notes: z.string().optional()
});
export type GroupCodeDraft = z.infer<typeof groupCodeDraftSchema>;

export const groupAccessCodeSchema = z.object({
  id: z.string(),
  groupName: z.string(),
  contactName: z.string(),
  contactEmail: z.string(),
  locationId: z.string(),
  spaceId: z.string(),
  source: z.literal('reception'),
  category: accessCodeCategorySchema,
  deliveryChannels: z.array(deliveryChannelSchema),
  status: z.enum(['active', 'scheduled']),
  passphrase: z.string(),
  validFrom: z.string(),
  validUntil: z.string(),
  devicesAllowed: z.number(),
  devicesConnected: z.number(),
  lastSeenAt: z.string().nullable(),
  qrPayload: z.string(),
  serviceMode: z.boolean(),
  tags: z.array(z.string()),
  notes: z.string().nullable()
});
export type GroupAccessCode = z.infer<typeof groupAccessCodeSchema>;

export const bulkUploadDraftSchema = z.object({
  locationId: z.string(),
  csvText: z.string(),
  mode: bulkModeSchema,
  defaultSpaceId: z.string(),
  deliveryChannels: z.array(deliveryChannelSchema),
  requestedBy: z.string(),
  ssid: z.string().optional(),
  serviceMode: z.boolean().optional().default(false)
});
export type BulkUploadDraft = z.infer<typeof bulkUploadDraftSchema>;

export const tenantDraftSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  brand: z.string().optional(),
  sla: z.string().optional(),
  dpskCoverage: z.number().optional()
});
export type TenantDraft = z.infer<typeof tenantDraftSchema>;

const vendorDraftSchema = z.object({
  name: z.string(),
  fields: vendorFieldMapSchema
});

export const controllerVendorDraftSchema = vendorDraftSchema;
export type ControllerVendorDraft = z.infer<typeof controllerVendorDraftSchema>;

export const pmsVendorDraftSchema = vendorDraftSchema;
export type PmsVendorDraft = z.infer<typeof pmsVendorDraftSchema>;

export const locationDraftSchema = z.object({
  id: z.string().optional(),
  tenantId: z.string(),
  name: z.string(),
  city: z.string(),
  category: locationCategorySchema,
  pms: optionalVendorBindingSchema,
  controller: optionalVendorBindingSchema,
  tvIntegration: z.boolean(),
  emailIntegration: z.boolean(),
  state: locationAdministrationStateSchema,
  note: z.string(),
  timezone: z.string()
});
export type LocationDraft = z.infer<typeof locationDraftSchema>;

export const unitDraftSchema = z.object({
  id: z.string().optional(),
  locationId: z.string(),
  name: z.string(),
  type: unitKindSchema,
  vlanId: z.number(),
  serviceVlanId: z.number().nullable().optional(),
  networkId: z.string().nullable().optional(),
  panMode: panModeSchema,
  capacity: z.number(),
  assignment: unitAssignmentSchema,
  status: unitStatusSchema
});
export type UnitDraft = z.infer<typeof unitDraftSchema>;
export const spaceDraftSchema = unitDraftSchema;
export type SpaceDraft = UnitDraft;

export const appUserDraftSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  email: z.string(),
  role: userRoleSchema,
  scopes: z.array(userScopeDraftSchema),
  status: userStatusSchema
});
export type AppUserDraft = z.infer<typeof appUserDraftSchema>;

export const channelMetricSchema = z.object({
  channel: deliveryChannelSchema,
  label: z.string(),
  count: z.number(),
  share: z.number()
});
export type ChannelMetric = z.infer<typeof channelMetricSchema>;

export const authenticatedUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  realmRoles: z.array(z.string()),
  tenantIds: z.array(z.string()),
  locationIds: z.array(z.string()),
  mode: authenticatedUserModeSchema
});
export type AuthenticatedUser = z.infer<typeof authenticatedUserSchema>;

export function userTenantIds(scopes: UserScope[]): string[] {
  return [...new Set(scopes.map((scope) => scope.tenantId))];
}

export function userLocationIds(scopes: UserScope[]): string[] {
  return [...new Set(scopes.flatMap((scope) => scope.locationIds))];
}
